<script setup lang="ts">
import ExportDetectionButton from '@/components/Buttons/components/ExportDetectionButton.vue'
import { FullTableLayout, StackLayout } from '@/components/Layouts'
import { BaseTable, TablePagination } from '@/components/Table'
import store from '@/store'
import { EDetectionActions } from '@/store/detectionStore/DetectionStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { FilterTagModel } from '@/types/components/FilterTagModel'
import { ESortBy, ESortByDate, getSortByKey } from '@/types/enum/SortByEnum'
import {
    type AdvancedDetectionFilters,
    type DetectionFilters
} from '@/types/filters/DetectionFilters'
import type { DetectionModel } from '@/types/models/DetectionModel'
import { DATETIME_FORMAT } from '@/utils/dateUtils'
import { getQueryFilters, getQuerySort } from '@/utils/queryUtils'
import { isMobile, isMobileEvent } from '@/utils/viewsUtils'
import moment from 'moment'
import { computed, ref, watch } from 'vue'
import DetectionsDesktopAdvancedFilters from './DetectionsDesktopAdvancedFilters.vue'
import DetectionsMobileAdvancedFilters from './DetectionsMobileAdvancedFilters.vue'

type Props = {
    storeAction?: EDetectionActions
    filters?: any

    exportFilters?: any
    hideExport?: boolean
    disabledExport?: boolean

    storeExportAction?: EDetectionActions
    exportFileNameData?: Record<string, any>
}
const props = defineProps<Props>()
type Emits = {
    (e: 'click', row: DetectionModel): void
    (e: 'changeFilters', filters: any): void
}
const emit = defineEmits<Emits>()

const data = computed(() => store.state.detection)
const storeFilters = computed(() => store.state.filters)

const localFilters = ref<Partial<AdvancedDetectionFilters & DetectionFilters>>({
    ...props.filters,
    ...getQueryFilters(),
    ...storeFilters.value
})
watch(
    () => props.filters,
    newValue => {
        localFilters.value = { ...localFilters.value, ...newValue }
        getDetections()
    },
    { deep: true }
)
const sort = ref<ESortBy>(getQuerySort() ?? ESortBy.DATE_RECENT)

const getDetections = () =>
    store.dispatch(
        `${EStoreModules.DETECTION}/${props.storeAction ?? EDetectionActions.FETCH_DETECTIONS}`,
        {
            filters: props.filters,
            sort: getSortByKey(sort.value),
            per_page: data.value.per_page,
            page: data.value.current_page
        }
    )

const handleChangePageSize = (value: number) => {
    data.value.per_page = value
    data.value.current_page = 1
    getDetections()
}

const handleChangePageNumber = (value: number) => {
    data.value.current_page = value
    getDetections()
}

const handleAdvancedFilters = (value: Partial<AdvancedDetectionFilters>) =>
    (advancedFilters.value = { ...advancedFilters.value, ...value })

const handleChangeSortBy = (sortBy: ESortBy) => {
    sort.value = sortBy
    getDetections()
}

const handleSearch = () => {
    localFilters.value = { ...localFilters.value, ...advancedFilters.value }
    data.value.current_page = 1
    getDetections()
}

const handleRowClick = (row: DetectionModel) => {
    data.value.current_page = 1
    emit('click', row)
}

const showMobileFilters = ref(isMobile())
isMobileEvent(e => (showMobileFilters.value = e.matches))
const table = ref<any>([])

const advancedFilters = ref<Partial<AdvancedDetectionFilters & DetectionFilters>>({
    ...getQueryFilters()
})
const filtersTags = computed(() =>
    Object.entries({
        start_at: localFilters.value.start_at
            ? moment(localFilters.value.start_at).utc().format(DATETIME_FORMAT.value).toString()
            : undefined,
        end_at: localFilters.value.end_at
            ? moment(localFilters.value.end_at).utc().format(DATETIME_FORMAT.value).toString()
            : undefined
    })
        .map(([name, value]) => ({
            name,
            value
        }))
        .filter(item => !!item.value)
)

const handleRemoveTag = (value: FilterTagModel) => {
    localFilters.value = { ...localFilters.value, [value.name]: undefined }
    advancedFilters.value = { ...advancedFilters.value, [value.name]: undefined }
    data.value.current_page = 1
    getDetections()
}
</script>

<template>
    <FullTableLayout
        :orderOptions="ESortByDate"
        :sortBy="sort"
        @search="handleSearch"
        @changeSortBy="handleChangeSortBy"
        :advancedFilters="filtersTags"
        translationPath="detection.filters"
        @removeTag="handleRemoveTag">
        <template #export>
            <ExportDetectionButton
                v-if="!hideExport"
                :sort="sort"
                :perPage="data.per_page"
                :disabled="disabledExport"
                :exportFilters="{ ...filters, ...(exportFilters ?? {}) }"
                :exportAction="storeExportAction"
                :exportFileNameData="exportFileNameData"
                :currentPage="data.current_page">
                <template v-if="$slots['exportFilters']" #exportFilters>
                    <slot name="exportFilters" />
                </template>
            </ExportDetectionButton>
        </template>
        <template #filters>
            <slot name="filters" />
        </template>

        <template #table>
            <BaseTable
                v-bind:ref="table"
                :data="data.data.detections"
                :loading="data.isLoading"
                @click="handleRowClick">
                <template #columns>
                    <slot name="columns" />
                </template>
            </BaseTable>
            <StackLayout :gap="8" class="centered-pagination border-table">
                <TablePagination
                    :perPage="data.per_page"
                    :currentPage="data.current_page"
                    :lastPage="data.last_page"
                    :total="data.total"
                    @change:pageSize="handleChangePageSize"
                    @change:pageNumber="handleChangePageNumber" />
            </StackLayout>
        </template>

        <template #dialogContent>
            <DetectionsMobileAdvancedFilters
                v-if="showMobileFilters"
                :filters="advancedFilters"
                @change="handleAdvancedFilters" />
            <DetectionsDesktopAdvancedFilters
                v-else
                :filters="advancedFilters"
                @change="handleAdvancedFilters" />
        </template>
    </FullTableLayout>
</template>
